/**
 * rebalanceService - Balanceamento automático de carga operacional
 *
 * Diagnostica carga por operador, calcula score composto,
 * simula redistribuição e executa movimentações aprovadas.
 */

import { supabase } from '../../../lib/supabaseClient';
import type { UnifiedWorkItem, TeamMemberWorkload } from '../types/workItem';
import { computeTeamWorkload } from './unifiedWorkService';

// ── Types ────────────────────────────────────────────────────

export interface RebalanceMove {
  item: UnifiedWorkItem;
  fromUserId: string;
  fromUserName: string;
  toUserId: string;
  toUserName: string;
}

export interface RebalanceImpact {
  slaViolatedBefore: number;
  slaViolatedAfter: number;
  bottleneckBefore: number;
  bottleneckAfter: number;
  maxLoadBefore: number;
  maxLoadAfter: number;
}

export interface RebalancePlan {
  moves: RebalanceMove[];
  impact: RebalanceImpact;
  membersBefore: TeamMemberWorkload[];
  membersAfter: TeamMemberWorkload[];
}

// ── Source → table mapping ───────────────────────────────────

const SOURCE_TABLE: Record<string, string> = {
  action: 'actions',
  workflow: 'workflow_tasks_v9',
  problem: 'issues',
  monitoring: 'actions', // monitoring items stored in actions
};

// ── Score calculation ────────────────────────────────────────

function operatorScore(m: TeamMemberWorkload): number {
  // Higher = more overloaded
  return (
    m.total_active * 1.0 +
    m.tasks_overdue * 3.0 +
    (m.sla_violated_pct / 100) * 2.0 +
    (m.bottleneck_score * 0.5)
  );
}

// ── Movability filter ────────────────────────────────────────

function isMovable(item: UnifiedWorkItem): boolean {
  if (item.status === 'completed') return false;
  if (item.status === 'waiting') return false; // blocked/waiting
  // in_progress items with long activity are less ideal to move,
  // but still movable — pending items are preferred
  return true;
}

// ── Priority sort weight (high first) ────────────────────────

function priorityWeight(p: string | null | undefined): number {
  if (p === 'high' || p === 'critical') return 0;
  if (p === 'medium') return 1;
  return 2;
}

// ── Simulate rebalance ──────────────────────────────────────

export function simulateRebalance(
  items: UnifiedWorkItem[],
  members: TeamMemberWorkload[],
): RebalancePlan {
  if (members.length < 2) {
    return { moves: [], impact: emptyImpact(members), membersBefore: members, membersAfter: members };
  }

  // Build mutable assignment map: userId → items[]
  const assignmentMap = new Map<string, UnifiedWorkItem[]>();
  for (const m of members) {
    assignmentMap.set(m.user_id, []);
  }
  for (const item of items) {
    if (!item.assigned_to || item.status === 'completed') continue;
    const list = assignmentMap.get(item.assigned_to);
    if (list) list.push(item);
  }

  // Build name lookup
  const nameMap = new Map<string, string>();
  for (const m of members) {
    nameMap.set(m.user_id, m.user_name);
  }

  const moves: RebalanceMove[] = [];

  // Iterative balancing: move from most loaded to least loaded
  // until max difference <= 1
  const MAX_ITERATIONS = 50; // safety cap
  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    const loads = [...assignmentMap.entries()].map(([uid, its]) => ({
      uid,
      active: its.filter(i => i.status !== 'completed').length,
    }));

    loads.sort((a, b) => b.active - a.active);
    const most = loads[0];
    const least = loads[loads.length - 1];

    if (most.active - least.active <= 1) break;

    // Find best movable item from most loaded
    const candidates = (assignmentMap.get(most.uid) ?? [])
      .filter(isMovable)
      .sort((a, b) => {
        // Prefer pending over in_progress (less disruption)
        const statusOrder = (s: string) => s === 'pending' ? 0 : 1;
        const sd = statusOrder(a.status) - statusOrder(b.status);
        if (sd !== 0) return sd;
        // Then prefer higher priority (move important tasks to someone with capacity)
        return priorityWeight(a.priority) - priorityWeight(b.priority);
      });

    if (candidates.length === 0) break; // no movable items left

    const itemToMove = candidates[0];

    // Move it
    const fromList = assignmentMap.get(most.uid)!;
    const idx = fromList.indexOf(itemToMove);
    if (idx >= 0) fromList.splice(idx, 1);

    const toList = assignmentMap.get(least.uid) ?? [];
    toList.push(itemToMove);
    assignmentMap.set(least.uid, toList);

    moves.push({
      item: itemToMove,
      fromUserId: most.uid,
      fromUserName: nameMap.get(most.uid) ?? most.uid.slice(0, 8),
      toUserId: least.uid,
      toUserName: nameMap.get(least.uid) ?? least.uid.slice(0, 8),
    });
  }

  // Compute impact by building virtual items with new assignments
  const virtualItems = items.map(original => {
    const move = moves.find(m => m.item.id === original.id);
    if (move) {
      return { ...original, assigned_to: move.toUserId };
    }
    return original;
  });

  const membersBefore = members;
  const membersAfter = computeTeamWorkload(virtualItems);

  const impact = computeImpact(membersBefore, membersAfter);

  return { moves, impact, membersBefore, membersAfter };
}

// ── Impact computation ───────────────────────────────────────

function computeImpact(before: TeamMemberWorkload[], after: TeamMemberWorkload[]): RebalanceImpact {
  const sumSla = (ms: TeamMemberWorkload[]) => {
    const total = ms.reduce((s, m) => s + m.sla_violated_pct, 0);
    return ms.length > 0 ? Math.round(total / ms.length) : 0;
  };
  const sumBn = (ms: TeamMemberWorkload[]) => {
    return ms.reduce((s, m) => s + m.bottleneck_score, 0);
  };
  const maxLoad = (ms: TeamMemberWorkload[]) => {
    return ms.reduce((mx, m) => Math.max(mx, m.total_active), 0);
  };

  return {
    slaViolatedBefore: sumSla(before),
    slaViolatedAfter: sumSla(after),
    bottleneckBefore: Math.round(sumBn(before) * 10) / 10,
    bottleneckAfter: Math.round(sumBn(after) * 10) / 10,
    maxLoadBefore: maxLoad(before),
    maxLoadAfter: maxLoad(after),
  };
}

function emptyImpact(members: TeamMemberWorkload[]): RebalanceImpact {
  return computeImpact(members, members);
}

// ── Execute approved moves ───────────────────────────────────

export async function executeRebalance(
  moves: RebalanceMove[],
  performedBy: string,
  organizationId: string,
): Promise<{ success: number; failed: number }> {
  let success = 0;
  let failed = 0;

  for (const move of moves) {
    const table = SOURCE_TABLE[move.item.source];
    if (!table) {
      failed++;
      continue;
    }

    const now = new Date().toISOString();

    const { error } = await supabase
      .from(table)
      .update({
        assigned_to: move.toUserId,
        updated_at: now,
      })
      .eq('id', move.item.source_id)
      .eq('organization_id', organizationId);

    if (error) {
      console.error(`[rebalance] Failed to move ${move.item.source_id}:`, error.message);
      failed++;
      continue;
    }

    // Log the reassignment
    await supabase
      .from('process_blueprint_logs')
      .insert({
        organization_id: organizationId,
        action: 'rebalance_reassign',
        performed_by: performedBy,
        details: {
          item_id: move.item.source_id,
          item_source: move.item.source,
          item_title: move.item.title,
          from_user: move.fromUserId,
          to_user: move.toUserId,
        },
        created_at: now,
      })
      .then(() => {}); // fire-and-forget log

    success++;
  }

  return { success, failed };
}
